<template>
  <ErrorBoundary>
    <div class="flex flex-col text-sm">
      <div class="mb-2">
        <PricingCategoryQuantityList
          v-model="categoryQuantities"
          size="lg"
          :categories="categoriesForList"
          :max-qty="isBoxEvent ? maxQty : undefined"
          :is-dates-loaded="isDatesLoaded"
          :loading-pricings="loadingPricings"
        />

        <div v-if="errorMessage" class="text-xs text-danger mt-2">
          {{ errorMessage }}
        </div>
      </div>
    </div>
  </ErrorBoundary>
</template>

<script lang="ts" setup>
import type {
  PricingCategory,
  EventDate,
  Pricings,
  EventDescription,
} from '~/composables/types/event'
import {
  sortCategoriesByType,
  type PriceBreakdownItem,
} from '~/composables/useFormattedPricingCategories'

const props = defineProps<{
  categories: PricingCategory[]
  selectedDate: EventDate | undefined
  isBookingAsPrivate: boolean
  isBoxEvent: boolean
  isDatesLoaded: boolean
  selectedTicketQuantity: number
  loadingPricings?: boolean
}>()

const { totalTicketsComputed } = useTicketQuantityCalculations()

// Inject pricing data from parent
const pricingsData = inject<Ref<Record<string, Pricings>>>(
  'pricingsData',
  ref({})
)

const { t } = useI18n()
const event = inject<EventDescription>('eventData')
const errorMessage = ref<string>('')
const { pricingData } = useEventPricingsGetters(pricingsData, {
  dateId: computed(() => props.selectedDate?.id),
  quantity: computed(() => props.selectedTicketQuantity),
  event: computed(() => event),
})

const { maxQty } = useTicketAvailability({
  selectedDate: computed(() => props.selectedDate),
  isBookingAsPrivate: computed(() => props.isBookingAsPrivate),
})

// Initialize quantities dynamically based on categories
// Default to 1 adult ticket
const categoryQuantities = ref<Record<string, number>>(
  props.categories.reduce(
    (acc, cat) => {
      acc[cat.ticket_category] = cat.ticket_category === 'ADULT' ? 1 : 0
      return acc
    },
    {} as Record<string, number>
  )
)

const totalTickets = totalTicketsComputed(categoryQuantities)

watch(
  () => props.selectedTicketQuantity,
  (newQuantity) => {
    if (totalTickets.value === newQuantity) return

    const defaultCategory =
      sortCategoriesByType(props.categories)[0]?.ticket_category
    if (!defaultCategory) return

    props.categories.forEach((cat) => {
      categoryQuantities.value[cat.ticket_category] =
        cat.ticket_category === defaultCategory ? newQuantity : 0
    })
  }
)

const { formatCategories } = useFormattedPricingCategories()

// Keep previous price breakdown to prevent flicker during transitions
const previousPriceBreakdown = ref<PriceBreakdownItem[] | undefined>(undefined)

const pricesByCategory = computed(() => {
  const pricingEntry = pricingData.value

  // If we have current pricing data, use it and update previous
  if (pricingEntry?.price_breakdown) {
    previousPriceBreakdown.value = pricingEntry.price_breakdown
    return pricingEntry.price_breakdown
  }

  // When loading OR when we just finished loading but don't have prices yet,
  // return undefined to show skeleton
  // This ensures skeleton stays visible until new prices are actually available
  if (props.loadingPricings) {
    return undefined
  }

  // Not loading and no current data - return undefined (skeleton will show)
  // This handles the case where loadingPricings becomes false but prices aren't ready yet
  return undefined
})

const categoriesForList = computed(() => {
  const staticPricingCategories =
    event?.pricing?.data?.pricingCategories?.data || []
  return formatCategories(props.categories, {
    pricesByCategory: pricesByCategory.value,
    sortCategories: true,
    staticPricingCategories,
    categoryQuantities: categoryQuantities.value,
    loadingPricings: props.loadingPricings,
    eventData: event,
  })
})

// Emit the total quantity for parent component
const emit = defineEmits<{
  (e: 'update:modelValue', value: number): void
  (e: 'update:categories', value: Record<string, number>): void
}>()

// Clear previous prices when date changes (prices are date-specific)
watch(
  () => props.selectedDate?.id,
  () => {
    previousPriceBreakdown.value = undefined
  }
)

// Watch for changes and emit
watch(
  categoryQuantities.value,
  (newQuantities) => {
    errorMessage.value = ''
    emit('update:modelValue', totalTickets.value)
    emit('update:categories', newQuantities)

    const maxReached =
      Object.values(newQuantities).reduce(
        (acc: number, quantity: number) => acc + quantity,
        0
      ) > maxQty.value
    if (maxReached) {
      errorMessage.value = t(
        'product.show.checkout.triedToAddBeyondMaxTickets',
        {
          n: maxQty.value,
        }
      )
    }
  },
  { deep: true }
)

// Watch for constraint violations is handled by useQuantityValidation composable
</script>
